import { ok, type Result } from 'neverthrow';
import type { Graph, NodeId, NodeInstance } from '../../../types/graph';
import type { MakeOptional } from '@/types';
import {
  CommandKind,
  type CommandResult,
  type CommandError,
  type GraphCommand,
} from '../types';
import {
  deleteGraphsForRemovedNodes,
  restoreGraphsForAddedNodes,
  withEffects,
} from '../effects';
import { bumpTopology, defaultIdFactory, getTouchingEdgeIds } from '../utils';

class AddNodeCommand implements GraphCommand {
  readonly kind = CommandKind.AddNode;

  constructor(public readonly node: MakeOptional<NodeInstance, 'id'>) {}

  apply(graph: Readonly<Graph>): Result<CommandResult, CommandError> {
    let id = this.node.id;
    if (!id) {
      id = defaultIdFactory.nodeId();
    }
    const nextNode: NodeInstance = {
      ...this.node,
      id,
    };
    const nextGraph: Graph = {
      ...graph,
      nodes: {
        ...graph.nodes,
        [id]: nextNode,
      },
    };
    const dirtyNodes = [id];
    const inverse = new RemoveNodeCommand(id);

    return ok({
      graph: bumpTopology(nextGraph),
      inverse,
      dirty: {
        nodes: dirtyNodes,
        edges: [],
        topologyChanged: true,
      },
    });
  }
}

export class RemoveNodeCommand implements GraphCommand {
  readonly kind = CommandKind.RemoveNode;

  constructor(public readonly id: NodeId) {}

  apply(graph: Readonly<Graph>): Result<CommandResult, CommandError> {
    const dirtyEdges = getTouchingEdgeIds(graph, this.id);
    const node = graph.nodes[this.id];
    const inverse = new AddNodeCommand(node);
    const nextNodes = { ...graph.nodes };
    delete nextNodes[this.id];
    const nextGraph: Graph = {
      ...graph,
      nodes: nextNodes,
    };

    return ok({
      graph: bumpTopology(nextGraph),
      inverse,
      dirty: {
        nodes: [],
        edges: dirtyEdges,
        topologyChanged: true,
      },
    });
  }
}

export const createAddNodeCommand = (
  node: MakeOptional<NodeInstance, 'id'>
) => {
  return withEffects(
    new AddNodeCommand(node),
    ({ before, after }) => restoreGraphsForAddedNodes(before, after),
    ({ inverse }) =>
      withEffects(inverse, ({ before, after }) =>
        deleteGraphsForRemovedNodes(before, after)
      )
  );
};
